import React, { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { z } from 'zod';
import { useSetFormFieldValue } from 'src/components/filters/helpers';
import { LggDropdownButtonWithoutOverlay } from 'src/components/general/button/dropdown-button';
import { LggPopover } from 'src/components/general/display/popover';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { Radio } from 'src/components/general/inputs/radio';
import { OverlayFilterActionButtons } from 'src/components/general/overlay/overlay-filter-action-buttons';
import { FlexColumn } from 'src/components/layout/flex-column';
import { AiAgentAnalyticsFiltersContext } from 'src/components/pages/ai-agents/analytics/components/ai-agent-analytics-filters';
import { AiAgentsSelect } from 'src/components/pages/ai-agents/analytics/components/ai-agents-select';
import { useAiAgentsListForSelect } from 'src/components/pages/ai-agents/analytics/hooks/use-ai-agents-list-for-select';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

const AgentFilterDropdown = styled(LggDropdownButtonWithoutOverlay)<{
  $isFiltered: boolean;
}>`
  border: 1px solid ${({ theme }) => theme.colors.koala};
  font-size: 13px;
  height: 32px;
  padding: 8px 10px;

  ${({ $isFiltered }) =>
    $isFiltered &&
    css`
      &,
      &:hover,
      &:focus {
        background: ${({ theme }) => theme.colors.secondaryTopaz10};
        border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz40};
      }
    `};

  ${up('md')} {
    border: none;
    font-size: 14px;
    height: 38px;
    padding: 10px 15px;
  }
`;

const ContextMenu = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  box-shadow: 0px 20px 40px 0px rgba(91, 101, 112, 0.1);
  padding: 20px 20px 15px;
  width: 296px;
`;

const FilterContent = styled.div`
  padding: 20px 20px 0;

  ${up('md')} {
    padding: 0;
  }
`;

const FilterTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
`;

const RadiosContainer = styled(FlexColumn)`
  gap: 28px;
  margin-top: 25px;
`;

const CustomAgentSelectContainer = styled.div`
  padding: 6px 0 0 30px;
`;

enum AgentFilterRadioValue {
  ALL = 'ALL',
  CUSTOM = 'CUSTOM',
}

const agentFilterFormSchema = z
  .object({
    owner: z.nativeEnum(AgentFilterRadioValue),
    aiAgentId: z.string().optional(),
  })
  .refine(
    ({ owner, aiAgentId }) => !(owner === AgentFilterRadioValue.CUSTOM && !aiAgentId),
    { path: ['aiAgentId'] },
  );

type AgentFilterFormValues = z.infer<typeof agentFilterFormSchema>;

const agentFilterDefaultValues: AgentFilterFormValues = {
  owner: AgentFilterRadioValue.ALL,
  aiAgentId: undefined,
};

export const AiAgentAnalyticsAgentFilter = () => {
  const { t } = useTranslation(['common', 'aiAgents']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const visibilityHandler = useVisible();
  const { aiAgentId, setAiAgentId } = useContext(AiAgentAnalyticsFiltersContext);
  const { aiAgents } = useAiAgentsListForSelect();

  const mainForm = useForm<AgentFilterFormValues>({
    resolver: zodResolver(agentFilterFormSchema),
    defaultValues: agentFilterDefaultValues,
  });

  useEffect(() => {
    const watcher = mainForm.watch(({ owner, aiAgentId }) => {
      setAiAgentId(owner === AgentFilterRadioValue.CUSTOM ? aiAgentId ?? null : null);
    });

    return () => watcher.unsubscribe();
  }, [mainForm, setAiAgentId]);

  const innerForm = useForm<AgentFilterFormValues>({
    resolver: zodResolver(agentFilterFormSchema),
    defaultValues: mainForm.getValues(),
  });

  const { setFormFieldValue } = useSetFormFieldValue<AgentFilterFormValues>({
    defaultValues: mainForm.getValues(),
    setValue: innerForm.setValue,
  });

  const allAgentsLabel = t('aiAgents:pages.analytics.filters.allAgents');
  const selectedLabel =
    aiAgents.find(({ id }) => id === aiAgentId)?.name ?? allAgentsLabel;

  const handleSubmit = () =>
    innerForm.handleSubmit(({ owner, aiAgentId }) => {
      mainForm.setValue('owner', owner);
      mainForm.setValue('aiAgentId', aiAgentId);
      visibilityHandler.close();
    })();

  const handleReset = () => {
    innerForm.reset(agentFilterDefaultValues);
    mainForm.reset(agentFilterDefaultValues);
    visibilityHandler.close();
  };

  const trigger = (
    <AgentFilterDropdown
      size="regular"
      variant="defaultWhite"
      icon="everyone"
      isActive={visibilityHandler.visible}
      onClick={visibilityHandler.show}
      $isFiltered={!breakpointUpMd && aiAgentId !== null}
      data-lgg-id="ai-agent-analytics-agent-filter-trigger"
    >
      {selectedLabel}
    </AgentFilterDropdown>
  );

  const close = () => {
    visibilityHandler.close();
    innerForm.reset(mainForm.getValues());
  };

  const content = (
    <FilterContent>
      <FilterTitle>{t('aiAgents:pages.analytics.filters.title')}</FilterTitle>
      <form>
        <Controller
          control={innerForm.control}
          name="owner"
          render={({ field: ownerField }) => (
            <RadiosContainer>
              <Radio
                value={AgentFilterRadioValue.ALL}
                text={allAgentsLabel}
                testId="ai-agent-analytics-agent-filter-radio-all"
                name="ai-agent-analytics-agent-filter-radio-all"
                checked={ownerField.value === AgentFilterRadioValue.ALL}
                onChange={() =>
                  innerForm.reset({
                    ...agentFilterDefaultValues,
                    owner: AgentFilterRadioValue.ALL,
                  })
                }
              />
              <span>
                <Radio
                  value={AgentFilterRadioValue.CUSTOM}
                  text={`${t('common:custom')}:`}
                  testId="ai-agent-analytics-agent-filter-radio-custom"
                  name="ai-agent-analytics-agent-filter-radio-custom"
                  checked={ownerField.value === AgentFilterRadioValue.CUSTOM}
                  onChange={() =>
                    setFormFieldValue('owner', AgentFilterRadioValue.CUSTOM)
                  }
                />
                <CustomAgentSelectContainer>
                  <Controller
                    control={innerForm.control}
                    name="aiAgentId"
                    render={({ field, fieldState }) => (
                      <AiAgentsSelect
                        name="ai-agent-analytics-agent-filter-select"
                        placeholder={t('aiAgents:pages.analytics.filters.selectAgent')}
                        isDisabled={ownerField.value !== AgentFilterRadioValue.CUSTOM}
                        hasError={Boolean(fieldState.error)}
                        selectedAiAgentId={field.value}
                        onChange={(option) => {
                          innerForm.clearErrors('aiAgentId');
                          innerForm.setValue('aiAgentId', option?.value);
                        }}
                      />
                    )}
                  />
                </CustomAgentSelectContainer>
              </span>
            </RadiosContainer>
          )}
        />
      </form>
    </FilterContent>
  );

  const actionButtons = (
    <OverlayFilterActionButtons
      onReset={handleReset}
      onSubmit={async (e) => {
        preventDefaultAndStopPropagation(e);
        await handleSubmit();
      }}
      baseTestId="ai-agent-analytics-agent-filter"
    />
  );

  if (breakpointUpMd) {
    return (
      <LggPopover
        visible={visibilityHandler.visible}
        content={
          <ContextMenu data-lgg-id="ai-agent-analytics-agent-filter-overlay">
            {content}
            {actionButtons}
          </ContextMenu>
        }
        trigger={['click']}
        overlayStyle={{ padding: 0 }}
        hideArrow
        onVisibleChange={(isVisible) => (isVisible ? visibilityHandler.show() : close())}
        placement="bottomRight"
      >
        <span>{trigger}</span>
      </LggPopover>
    );
  }

  return (
    <>
      {trigger}
      <BottomDrawer
        visible={visibilityHandler.visible}
        title={t('common:filters.title')}
        onClose={close}
        fullHeight
        footer={actionButtons}
      >
        {content}
      </BottomDrawer>
    </>
  );
};
